import { canDockAt, nearestStation, stationDefinition, type StationId } from "@frontier/content/stations";
import type { FactionId } from "@frontier/content/factions";
import type {
  GateSnapshot,
  RegionSnapshot,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import type { Point } from "./game/geometry";

export type GalaxyTarget = { kind: "region"; id: string } | { kind: "station"; id?: StationId };

interface GalaxyEdge {
  fromSectorId: string;
  toSectorId: string;
  gateIds: readonly string[];
}
interface GalaxyDestination {
  point: Point;
  label: string;
  sectorIds: readonly string[];
  gateId: string | null;
}
interface DirectedGate {
  fromSectorId: string;
  gate: GateSnapshot;
}
interface GateRoute {
  gate: GateSnapshot;
  sectorIds: readonly string[];
}

function validRegion(world: WorldSnapshot, region: RegionSnapshot): boolean {
  return (
    region.id.length > 0 &&
    Number.isFinite(world.width) &&
    Number.isFinite(world.height) &&
    world.width > 0 &&
    world.height > 0 &&
    Number.isFinite(region.x) &&
    Number.isFinite(region.y) &&
    Number.isFinite(region.width) &&
    Number.isFinite(region.height) &&
    region.x >= 0 &&
    region.y >= 0 &&
    region.width > 0 &&
    region.height > 0 &&
    region.x + region.width <= world.width &&
    region.y + region.height <= world.height
  );
}

export function galaxyRegionAt(
  world: WorldSnapshot,
  point: Point & { sectorId?: string },
): RegionSnapshot | undefined {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return undefined;
  if (world.boundaryRules && point.sectorId !== undefined)
    return world.regions.find(region => region.id === point.sectorId && validRegion(world, region));
  return world.regions.find(
    (region) =>
      validRegion(world, region) &&
      point.x >= region.x &&
      point.x < region.x + region.width &&
      point.y >= region.y &&
      point.y < region.y + region.height,
  );
}

function directedGates(world: WorldSnapshot): DirectedGate[] {
  const regions = new Set(
    world.regions
      .filter((region) => validRegion(world, region))
      .map((region) => region.id),
  );
  return world.gates.flatMap((gate) => {
    const source = galaxyRegionAt(world, gate);
    return source &&
      gate.id.length > 0 &&
      Number.isFinite(gate.radius) &&
      gate.radius > 0 &&
      regions.has(gate.toSectorId)
      ? [{ fromSectorId: source.id, gate }]
      : [];
  });
}

export function buildGalaxyGraph(world: WorldSnapshot): readonly GalaxyEdge[] {
  const grouped = new Map<string, Map<string, Set<string>>>();
  for (const { fromSectorId, gate } of directedGates(world)) {
    let destinations = grouped.get(fromSectorId);
    if (!destinations) {
      destinations = new Map();
      grouped.set(fromSectorId, destinations);
    }
    let gates = destinations.get(gate.toSectorId);
    if (!gates) {
      gates = new Set();
      destinations.set(gate.toSectorId, gates);
    }
    gates.add(gate.id);
  }
  return [...grouped.keys()].sort().flatMap((fromSectorId) => {
    const destinations = grouped.get(fromSectorId);
    if (!destinations) return [];
    return [...destinations.keys()].sort().map((toSectorId) => ({
      fromSectorId,
      toSectorId,
      gateIds: [...(destinations.get(toSectorId) ?? [])].sort(),
    }));
  });
}

function compareGateIds(left: DirectedGate, right: DirectedGate): number {
  return left.gate.id < right.gate.id
    ? -1
    : left.gate.id > right.gate.id
      ? 1
      : 0;
}

function gateRoute(
  world: WorldSnapshot,
  pilot: Point,
  destinationSectorId: string,
): GateRoute | null {
  const origin = galaxyRegionAt(world, pilot);
  if (
    !origin ||
    origin.id === destinationSectorId ||
    !world.regions.some(
      (region) =>
        region.id === destinationSectorId && validRegion(world, region),
    )
  )
    return null;
  const gates = directedGates(world).sort(compareGateIds);
  const scale = Math.max(world.width, world.height);
  const distance = (gate: GateSnapshot) =>
    Math.hypot((gate.x - pilot.x) / scale, (gate.y - pilot.y) / scale);
  const local = gates
    .filter((entry) => entry.fromSectorId === origin.id)
    .sort(
      (left, right) =>
        distance(left.gate) - distance(right.gate) ||
        compareGateIds(left, right),
    );
  const queue: {
    sectorId: string;
    first: GateSnapshot | null;
    sectorIds: readonly string[];
  }[] = [{ sectorId: origin.id, first: null, sectorIds: [origin.id] }];
  const visited = new Set([origin.id]);
  for (const current of queue) {
    const outgoing = current.first === null ? local : gates;
    for (const { fromSectorId, gate } of outgoing) {
      if (fromSectorId !== current.sectorId || visited.has(gate.toSectorId))
        continue;
      const first = current.first ?? gate;
      const sectorIds = [...current.sectorIds, gate.toSectorId];
      if (gate.toSectorId === destinationSectorId)
        return { gate: first, sectorIds };
      visited.add(gate.toSectorId);
      queue.push({ sectorId: gate.toSectorId, first, sectorIds });
    }
  }
  return null;
}

export function firstLocalGate(
  world: WorldSnapshot,
  pilot: Point,
  destinationSectorId: string,
): GateSnapshot | null {
  return gateRoute(world, pilot, destinationSectorId)?.gate ?? null;
}

export function galaxyDestination(
  world: WorldSnapshot,
  pilot: Point & { factionId?: FactionId | null },
  target: GalaxyTarget,
): GalaxyDestination | null {
  const origin = galaxyRegionAt(world, pilot);
  const station = target.kind === "station" && target.id
    ? stationDefinition(target.id) : nearestStation(pilot, pilot.factionId ?? null);
  if (target.kind === "station" && !canDockAt(station, pilot.factionId ?? null)) return null;
  const destination =
    target.kind === "station"
      ? galaxyRegionAt(world, station)
      : world.regions.find(
          (region) => region.id === target.id && validRegion(world, region),
        );
  if (!origin || !destination) return null;
  const label = target.kind === "station" ? `Станция «${station.name}»` : destination.name;
  if (target.kind === "station" && origin.id === destination.id)
    return { point: { x: station.x, y: station.y }, label, sectorIds: [origin.id], gateId: null };
  const route = gateRoute(world, pilot, destination.id);
  return route
    ? {
        point: { x: route.gate.x, y: route.gate.y },
        label,
        sectorIds: route.sectorIds,
        gateId: route.gate.id,
      }
    : null;
}
